/**
 * The vector primitives every gravity-based block shares.
 *
 * Extracted from `rotation-tracking.ts`, which defined them privately and was their only consumer
 * while Angle was the only block reading an accelerometer. Tilt reads the same gravity vector
 * and needs the same four operations, and a second copy of `unit`'s zero-length guard is exactly
 * the kind of duplication that drifts: one copy gets a tolerance change and the other does not,
 * and the two blocks then disagree about which samples are usable.
 *
 * Deliberately small. These are arithmetic, not geometry — nothing here knows about gravity,
 * device axes or degrees of tilt. What a given axis MEANS is each block's own business, which is
 * why `rotation-tracking.ts` keeps its pivot derivation and Tilt will keep its own reading.
 */
/** Radians to degrees, for callers turning a `Math.acos`/`atan2` result into a reading. */
export const DEG = 180 / Math.PI;
export const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross = (a, b) => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
});
export const norm = (a) => Math.sqrt(dot(a, a));
export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
/** Null for a zero-length or non-finite sample, so callers cannot accidentally divide by zero. */
export function unit(a) {
    const n = norm(a);
    if (!Number.isFinite(n) || n < 1e-9)
        return null;
    return { x: a.x / n, y: a.y / n, z: a.z / n };
}
//# sourceMappingURL=vec3.js.map